/**
 * Category Budget Alert Service
 * 
 * API service for category-specific budget limit management.
 */

import { apiClient } from '../client';

export interface CategoryBudgetLimit {
  id: string;
  organizationId: string;
  category: string;
  fiscalYear: number;
  limitAmount: number | null;
  warningPercent: number;
  criticalPercent: number;
  exceededPercent: number;
  emailEnabled: boolean;
  notificationEnabled: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CategorySpendingStatus {
  category: string;
  fiscalYear: number;
  totalSpent: number;
  transactionCount: number;
  limitAmount: number | null;
  warningPercent: number;
  criticalPercent: number;
  exceededPercent: number;
  spentPercent: number;
  alertLevel: 'normal' | 'info' | 'warning' | 'critical';
  remainingAmount: number | null;
}

export interface CategoryAlertHistoryItem {
  id: string;
  category: string;
  fiscalYear: number;
  alertLevel: string;
  message: string;
  status: string;
  createdAt: string;
}

export interface CreateCategoryLimitRequest {
  organization_id: string;
  category: string;
  fiscal_year?: number;
  limit_amount?: number;
  warning_percent?: number;
  critical_percent?: number;
  exceeded_percent?: number;
  email_enabled?: boolean;
  notification_enabled?: boolean;
}

export interface UpdateCategoryLimitRequest {
  organization_id: string;
  limit_amount?: number | null;
  warning_percent?: number;
  critical_percent?: number;
  exceeded_percent?: number;
  email_enabled?: boolean;
  notification_enabled?: boolean;
  is_active?: boolean;
}

export class CategoryBudgetAlertService {
  /**
   * List all category budget limits for an organization
   */
  async listLimits(organizationId: string, fiscalYear?: number): Promise<CategoryBudgetLimit[]> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (fiscalYear) params.append('fiscal_year', fiscalYear.toString());
    
    const response = await apiClient.get<{ limits: CategoryBudgetLimit[] }>(
      `/category-budget-limits?${params.toString()}`
    );
    return response.limits;
  }

  /**
   * Get a specific category budget limit
   */
  async getLimit(limitId: string, organizationId: string): Promise<CategoryBudgetLimit> {
    const response = await apiClient.get<{ limit: CategoryBudgetLimit }>(
      `/category-budget-limits/${limitId}?organization_id=${organizationId}`
    );
    return response.limit;
  }

  /**
   * Create a new category budget limit
   */
  async createLimit(data: CreateCategoryLimitRequest): Promise<{ limit_id: string }> {
    return apiClient.post<{ limit_id: string }>('/category-budget-limits/', data);
  }

  /**
   * Update a category budget limit
   */
  async updateLimit(limitId: string, updates: UpdateCategoryLimitRequest): Promise<void> {
    await apiClient.put(`/category-budget-limits/${limitId}`, updates);
  }

  /**
   * Delete a category budget limit
   */
  async deleteLimit(limitId: string, organizationId: string): Promise<void> {
    await apiClient.delete(`/category-budget-limits/${limitId}?organization_id=${organizationId}`);
  }

  /**
   * Get spending status for all categories
   */
  async getSpendingStatus(organizationId: string, fiscalYear?: number): Promise<CategorySpendingStatus[]> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (fiscalYear) params.append('fiscal_year', fiscalYear.toString());
    
    const response = await apiClient.get<{ statuses: CategorySpendingStatus[]; fiscalYear: number }>(
      `/category-budget-limits/status?${params.toString()}`
    );
    return response.statuses;
  }

  /**
   * Get alert history for categories
   */
  async getHistory(organizationId: string, options?: {
    fiscalYear?: number;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ history: CategoryAlertHistoryItem[]; pagination: { limit: number; offset: number } }> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (options?.fiscalYear) params.append('fiscal_year', options.fiscalYear.toString());
    if (options?.category) params.append('category', options.category);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    
    return apiClient.get<{ history: CategoryAlertHistoryItem[]; pagination: { limit: number; offset: number } }>(
      `/category-budget-limits/history/list?${params.toString()}`
    );
  }

  /**
   * Trigger manual category budget alert check
   */
  async triggerCheck(organizationId: string, options?: {
    fiscal_year?: number;
    categories?: string[];
  }): Promise<{
    success: boolean;
    processed: number;
    triggered: number;
    errors: string[];
    message: string;
  }> {
    return apiClient.post('/category-budget-limits/check/trigger', {
      organization_id: organizationId,
      fiscal_year: options?.fiscal_year,
      categories: options?.categories,
    });
  }
}

// Export singleton instance
export const categoryBudgetAlertService = new CategoryBudgetAlertService();
